import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, ArrowRight, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reactivate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');

  const [step, setStep] = useState('email'); // email | password | loading | success | active | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // null | new_user | active | trialing | cancelled
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-fill email if user is logged in OR from URL param (redirect from Register popup)
  useEffect(() => {
    const urlEmail = searchParams.get('email');
    if (urlEmail) {
      setEmail(decodeURIComponent(urlEmail));
    } else {
      const storedEmail = localStorage.getItem('user_email');
      if (storedEmail) setEmail(storedEmail);
    }
  }, [searchParams]);

  const checkEmail = async (e) => {
    e.preventDefault();
    setError('');
    setCheckingEmail(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/check-email`, { email });
      const status = res.data.status;
      setEmailStatus(status);

      if (status === 'new_user') {
        // Neuer Nutzer → normale Registrierung mit Affiliate-Code
        const url = refCode
          ? `/register-enhanced?ref=${refCode}`
          : '/register-enhanced';
        navigate(url);
      } else if (status === 'active' || status === 'trialing') {
        setStep('active');
      } else {
        // cancelled → Passwort abfragen
        setStep('password');
      }
    } catch {
      setError('Fehler beim Prüfen der E-Mail. Bitte versuche es erneut.');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleReactivate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        email,
        password,
        frontend_url: window.location.origin,
      };
      if (refCode) payload.ref_code = refCode;

      const res = await axios.post(`${API_URL}/api/auth/reactivate`, payload);
      // Weiterleitung zum Stripe-Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Ein Fehler ist aufgetreten.';
      if (detail.includes('aktives Abonnement')) {
        setStep('active');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>AllFlowX</div>

        {/* Header */}
        {refCode && (
          <div style={styles.partnerBadge} data-testid="partner-badge">
            Empfohlen von einem Partner
          </div>
        )}

        {/* Schritt: E-Mail */}
        {step === 'email' && (
          <>
            <h2 style={styles.title}>Zurück zu AllFlowX</h2>
            <p style={styles.subtitle}>
              War du schon dabei? Gib deine E-Mail ein – wir erkennen deinen Status automatisch.
            </p>
            {error && <div style={styles.errorBox} data-testid="reactivate-error">{error}</div>}
            <form onSubmit={checkEmail}>
              <div style={styles.field}>
                <label style={styles.label}>E-Mail-Adresse</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={styles.input}
                  placeholder="deine@email.de"
                  required
                  autoFocus
                  data-testid="reactivate-email-input"
                />
              </div>
              <button
                type="submit"
                style={styles.btn}
                disabled={checkingEmail}
                data-testid="reactivate-email-submit"
              >
                {checkingEmail
                  ? <Loader2 style={{ width: 18, animation: 'spin 1s linear infinite' }} />
                  : <><span>Weiter</span><ArrowRight style={{ width: 18 }} /></>}
              </button>
            </form>
            <p style={styles.hint}>
              Noch kein Konto?{' '}
              <a href={refCode ? `/register-enhanced?ref=${refCode}` : '/register-enhanced'} style={styles.link}>
                Jetzt registrieren
              </a>
            </p>
          </>
        )}

        {/* Schritt: Passwort + Reaktivierung */}
        {step === 'password' && (
          <>
            <div style={styles.welcomeBack} data-testid="welcome-back-box">
              <div style={styles.wbIcon}><RefreshCw style={{ width: 24, color: '#e63946' }} /></div>
              <h2 style={styles.title}>Schön, dich wiederzusehen!</h2>
              <p style={styles.subtitle}>
                Dein Profil ist noch da. Melde dich an, um ohne Testphase sofort loszulegen.
              </p>
            </div>
            {refCode && (
              <div style={styles.affiliateNote}>
                Ein Partner wartet darauf, dich wieder willkommen zu heißen!
              </div>
            )}
            {error && <div style={styles.errorBox} data-testid="reactivate-error">{error}</div>}
            <form onSubmit={handleReactivate}>
              <div style={styles.field}>
                <label style={styles.label}>E-Mail</label>
                <div style={{ ...styles.input, background: '#f9fafb', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                  {email}
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Passwort</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Dein Passwort"
                  required
                  autoFocus
                  data-testid="reactivate-password-input"
                />
              </div>
              <div style={styles.noTrialBadge}>
                Kein Testzeitraum – Abonnement startet sofort
              </div>
              <button
                type="submit"
                style={{ ...styles.btn, opacity: loading ? 0.7 : 1 }}
                disabled={loading}
                data-testid="reactivate-submit"
              >
                {loading
                  ? <><Loader2 style={{ width: 18 }} /><span>Weiterleitung zu Stripe...</span></>
                  : <><span>Jetzt reaktivieren</span><ArrowRight style={{ width: 18 }} /></>}
              </button>
              <button type="button" onClick={() => setStep('email')} style={styles.backBtn}>
                Andere E-Mail verwenden
              </button>
            </form>
          </>
        )}

        {/* Status: Bereits aktiv */}
        {step === 'active' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }} data-testid="already-active">
            <CheckCircle style={{ width: 48, height: 48, color: '#059669', margin: '0 auto 16px' }} />
            <h2 style={styles.title}>Dein Account ist aktiv!</h2>
            <p style={styles.subtitle}>Du hast bereits ein aktives Abonnement bei AllFlowX.</p>
            <button onClick={() => navigate('/login')} style={styles.btn} data-testid="go-to-login">
              Zum Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', fontFamily: 'Arial, sans-serif',
  },
  card: {
    background: '#fff', borderRadius: '14px', padding: '40px',
    width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  logo: { fontSize: '22px', fontWeight: '800', color: '#e63946', marginBottom: '8px', textAlign: 'center' },
  partnerBadge: {
    background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '20px',
    padding: '4px 12px', fontSize: '12px', fontWeight: '600', color: '#92400e',
    textAlign: 'center', marginBottom: '16px',
  },
  title: { fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0 0 8px', textAlign: 'center' },
  subtitle: { fontSize: '14px', color: '#6b7280', textAlign: 'center', marginBottom: '24px', lineHeight: '1.5' },
  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
    padding: '12px', color: '#dc2626', fontSize: '13px', marginBottom: '16px',
  },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: {
    width: '100%', padding: '11px 14px', border: '1px solid #d1d5db',
    borderRadius: '7px', fontSize: '14px', color: '#111827', boxSizing: 'border-box',
  },
  btn: {
    width: '100%', padding: '13px', background: '#e63946', color: '#fff',
    border: 'none', borderRadius: '7px', fontSize: '15px', fontWeight: '700',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    marginTop: '4px',
  },
  backBtn: {
    width: '100%', padding: '10px', background: 'transparent', color: '#6b7280',
    border: 'none', fontSize: '13px', cursor: 'pointer', marginTop: '8px',
  },
  hint: { fontSize: '13px', color: '#6b7280', textAlign: 'center', marginTop: '16px' },
  link: { color: '#e63946', textDecoration: 'none', fontWeight: '600' },
  welcomeBack: { textAlign: 'center', marginBottom: '4px' },
  wbIcon: { display: 'flex', justifyContent: 'center', marginBottom: '12px' },
  affiliateNote: {
    background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '6px',
    padding: '10px 14px', fontSize: '13px', color: '#065f46', textAlign: 'center',
    marginBottom: '16px',
  },
  noTrialBadge: {
    background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px',
    padding: '8px 12px', fontSize: '12px', color: '#92400e', textAlign: 'center',
    marginBottom: '12px', fontWeight: '600',
  },
};
